import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { MindARThree } from 'mind-ar/dist/mindar-pose-three.prod.js';
import { FilterAndCaptureButtons } from '../../utils/screenCapture';
import { useParams } from 'react-router-dom';
import config from '../../config/config';
import './Necklace.css';

// MediaPipe Pose shoulder landmark indices
const LEFT_SHOULDER = 11;
const RIGHT_SHOULDER = 12;
const NECKLACE_SCALE = 0.5;

const getManifestScale = (manifest, fallback) => {
  const scale = Number(manifest?.scale);
  return Number.isFinite(scale) && scale > 0 ? scale : fallback;
};

function Necklace() {
  const { sku } = useParams();
  const category = 'necklaces';
  const containerRef = useRef(null);
  const mindarThreeRef = useRef(null);
  const sceneRef = useRef(null);
  const planeRef = useRef(null);
  const anchorRef = useRef(null);
  const streamRef = useRef(null);
  const modelsRef = useRef({ necklace: null });
  const [selectedFilter, setSelectedFilter] = useState('none');
  const [error, setError] = useState(null);

  const getRendererInfo = () => ({
    mindarThree: mindarThreeRef.current,
    models: modelsRef.current,
    plane: planeRef.current,
  });

  const fetchManifest = useCallback(async () => {
    try {
      const res = await fetch(`${config.cdnUrl}/models/2D/${category}/${sku}/manifest.json`);
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }, [sku]);

  const initializeScene = useCallback((mindarThree) => {
    const { renderer, scene, camera } = mindarThree;

    // Dispose any previous plane
    if (planeRef.current && anchorRef.current) {
      anchorRef.current.group.remove(planeRef.current);
      planeRef.current.geometry?.dispose();
      planeRef.current.material?.map?.dispose();
      planeRef.current.material?.dispose();
      planeRef.current = null;
      modelsRef.current.necklace = null;
    }

    const textureLoader = new THREE.TextureLoader();
    const loadTexture = (url) =>
      new Promise((resolve, reject) => {
        textureLoader.load(
          url,
          (tex) => {
            tex.encoding = THREE.sRGBEncoding;
            tex.minFilter = THREE.LinearFilter;
            tex.magFilter = THREE.LinearFilter;
            resolve(tex);
          },
          undefined,
          reject,
        );
      });

    const textureUrl = `${config.cdnUrl}/models/2D/${category}/${sku}/texture.png`;

    Promise.all([
      loadTexture(textureUrl).catch(() => loadTexture('/assets/2D/necklaces/default.png')),
      fetchManifest(),
    ]).then(([loadedTexture, manifest]) => {
      console.log('[2D Necklace] manifest:', JSON.stringify(manifest, null, 2));
      console.log('[2D Necklace] resolved offset ->', manifest?.offsets ?? { x: 0, y: -0.05, z: -0.1 });
      console.log('[2D Necklace] resolved scale ->', getManifestScale(manifest, NECKLACE_SCALE));
      const { width, height } = loadedTexture.image;
      const aspectRatio = width / height;
      const offset = manifest?.offsets ?? { x: 0, y: -0.05, z: -0.1 };
      const scale = getManifestScale(manifest, NECKLACE_SCALE);

      const geometry = new THREE.PlaneGeometry(aspectRatio, 1);
      const material = new THREE.MeshBasicMaterial({
        map: loadedTexture,
        transparent: true,
        depthTest: true,
        depthWrite: true,
        side: THREE.DoubleSide,
        alphaTest: 0.1,
      });

      // Clean up again in case of race condition
      if (planeRef.current && anchorRef.current) {
        anchorRef.current.group.remove(planeRef.current);
        planeRef.current.geometry?.dispose();
        planeRef.current.material?.map?.dispose();
        planeRef.current.material?.dispose();
        planeRef.current = null;
        modelsRef.current.necklace = null;
      }

      const plane = new THREE.Mesh(geometry, material);
      plane.scale.set(scale, scale, 1);
      plane.position.set(offset.x, offset.y, offset.z);
      plane.userData.baseScale = scale;

      planeRef.current = plane;
      modelsRef.current.necklace = plane;
      anchorRef.current.group.add(plane);
    }).catch((err) => {
      console.error('Error loading necklace texture:', err);
      setError('Failed to load necklace overlay');
    });

    // Lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.75);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(0, 0, 1);
    camera.add(directionalLight);
    scene.add(camera);

    return { renderer, scene, camera };
  }, [sku, fetchManifest]);

  useEffect(() => {
    let mounted = true;
    let mindarThree = null;

    const setup = async () => {
      try {
        if (!mounted) return;

        // Pre-acquire camera so the user sees the permission prompt before init.
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'user' },
            audio: false,
          });
          streamRef.current = stream;
        } catch (err) {
          console.error('Camera access error:', err);
          setError('Please allow camera access to use this feature');
          return;
        }

        // Clean up any leftover instance
        if (mindarThreeRef.current) {
          if (planeRef.current && anchorRef.current) {
            anchorRef.current.group.remove(planeRef.current);
            planeRef.current.geometry?.dispose();
            planeRef.current.material?.map?.dispose();
            planeRef.current.material?.dispose();
            planeRef.current = null;
            modelsRef.current.necklace = null;
          }
          await mindarThreeRef.current.stop();
          mindarThreeRef.current.renderer.setAnimationLoop(null);
          mindarThreeRef.current.renderer.dispose();
        }

        mindarThree = new MindARThree({
          container: containerRef.current,
          filterMinCF: 0.001,
          filterBeta: 1,
        });

        // Anchor at the midpoint between left (11) and right (12) shoulders.
        const necklaceAnchor = mindarThree.addMidpointAnchor(LEFT_SHOULDER, RIGHT_SHOULDER);
        anchorRef.current = necklaceAnchor;

        const sceneData = initializeScene(mindarThree);
        sceneRef.current = sceneData;

        if (mounted) {
          // Release the pre-acquired stream so MindAR's own stream is the only active one
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
          }
          mindarThreeRef.current = mindarThree;
          await mindarThree.start();

          sceneData.renderer.setAnimationLoop(() => {
            sceneData.renderer.render(sceneData.scene, sceneData.camera);
          });
        }
      } catch (err) {
        console.error('Setup error:', err);
        setError('Failed to initialize AR experience');
        if (mindarThree) await mindarThree.stop();
      }
    };

    setup();

    return () => {
      mounted = false;
      const cleanup = async () => {
        try {
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
          }
          if (mindarThreeRef.current) {
            if (planeRef.current && anchorRef.current) {
              anchorRef.current.group.remove(planeRef.current);
              planeRef.current.geometry?.dispose();
              planeRef.current.material?.map?.dispose();
              planeRef.current.material?.dispose();
              planeRef.current = null;
            }
            await mindarThreeRef.current.stop();
            mindarThreeRef.current.renderer.setAnimationLoop(null);
            mindarThreeRef.current.renderer.dispose();
          }
          planeRef.current = null;
          anchorRef.current = null;
          sceneRef.current = null;
          mindarThreeRef.current = null;
        } catch (err) {
          console.error('Cleanup error:', err);
        }
      };
      cleanup();
    };
  }, [initializeScene]);

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">
          {error}
          <button
            onClick={() => { setError(null); window.location.reload(); }}
            className="retry-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="ar-container" ref={containerRef}></div>
      <FilterAndCaptureButtons
        getRendererInfo={getRendererInfo}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        mode="necklace"
      />
    </div>
  );
}

export default Necklace;
